import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Play, Plus, Trash2, X } from "lucide-react";
import type { OpenRouterModel } from "../api/client";
import type { ExecCheckRecord, ExecResult } from "../api/workbench";
import type { CorpusSet } from "../types";
import {
  conditionSummary, destinationIssues, maxTokensForModel, parseReturnsFields, promptTokens, tauForModel, unboundTokens,
  ROLE_LABEL, type AgentCondition, type AgentDefV2, type BundleMapV2, type WorkbenchMeta,
} from "./types";

/* ===================================================================
   Agent Builder — one component for edit and create (and read-only
   version inspection). Everything an agent is, in one form: identity,
   Fires when (condition rows), Uses (inputs), Prompt (token-checked),
   Returns (output contract + destinations), Runs on, and a live Test
   with a Trigger → Prompt → Response → Effects stepper.
=================================================================== */

export interface AgentBuilderProps {
  agentId: string | null; // null = creating
  initial: AgentDefV2;
  bundle: BundleMapV2;
  meta: WorkbenchMeta;
  models: OpenRouterModel[];
  allowlist: string[];
  evaluatorModel: string;
  readOnly: boolean;
  corpuses: CorpusSet[];
  onSave: (id: string, def: AgentDefV2) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
  /** Persists the draft with this (unsaved) def, runs the email, returns the
      result. onProgress fires per agent as the pipeline streams. */
  onTestRun: (id: string, def: AgentDefV2, emailServerId: string, onProgress?: () => void) => Promise<ExecResult>;
}

const AMBER = "var(--amber)";

function slugify(name: string): string {
  return (
    name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 24) || "agent"
  );
}

function Section(p: { title: string; note?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="sec">
      <span className="cap">
        {p.title}
        {p.note && <span className="sub">— {p.note}</span>}
      </span>
      {p.children}
    </div>
  );
}

/** Prototype token classes: blue = built-in feed, green = agent output, amber = custom feed / tool. */
function tokClass(boundVia: string | undefined, unbound: boolean): string {
  if (unbound) return "tokpill bad";
  if (boundVia?.startsWith("agent:") || boundVia === "") return "tokpill agent";
  if (boundVia?.startsWith("feed:") || boundVia?.startsWith("tool:")) return "tokpill feed";
  return "tokpill";
}

/* Grouped deterministic-condition menu. */
function CondMenu(p: { meta: WorkbenchMeta; onPick: (id: string) => void; onClose: () => void }): JSX.Element {
  const groups = useMemo(() => {
    const g = new Map<string, typeof p.meta.signals>();
    for (const s of p.meta.signals) {
      if (!g.has(s.group)) g.set(s.group, []);
      g.get(s.group)!.push(s);
    }
    return [...g.entries()];
  }, [p.meta]);
  return (
    <div
      className="card"
      style={{ position: "absolute", zIndex: 40, top: "100%", left: 0, marginTop: 4, padding: 8, minWidth: 300, maxHeight: 320, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.14)" }}
    >
      {groups.map(([group, sigs]) => (
        <div key={group} style={{ marginBottom: 6 }}>
          <div className="up tert" style={{ fontSize: 10, padding: "4px 6px" }}>{group}</div>
          {sigs.map((s) => (
            <button
              key={s.id}
              className="btn btn-ghost btn-sm"
              style={{ display: "flex", width: "100%", justifyContent: "flex-start", textTransform: "none" }}
              onClick={() => { p.onPick(s.id); p.onClose(); }}
            >
              {s.label}
              {s.providers.length > 0 && <span className="tert" style={{ marginLeft: "auto", fontSize: 10.5 }}>from {s.providers.join("/")}</span>}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/* Grouped input picker. */
function InputPicker(p: {
  meta: WorkbenchMeta;
  bundle: BundleMapV2;
  selfId: string | null;
  chosen: Set<string>;
  onPick: (id: string) => void;
  onClose: () => void;
}): JSX.Element {
  const [q, setQ] = useState("");
  const agents = Object.entries(p.bundle).filter(([id]) => id !== p.selfId);
  const match = (s: string): boolean => s.toLowerCase().includes(q.toLowerCase());
  return (
    <div className="card" style={{ position: "absolute", zIndex: 40, top: "100%", left: 0, marginTop: 4, padding: 8, minWidth: 320, maxHeight: 340, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.14)" }}>
      <input className="inp" autoFocus placeholder="Search inputs…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: "100%", marginBottom: 6 }} />
      <div className="up tert" style={{ fontSize: 10, padding: "4px 6px" }}>Built-in feeds</div>
      {p.meta.builtinInputs.filter((b) => !p.chosen.has(b.id) && match(b.label)).map((b) => (
        <button key={b.id} className="btn btn-ghost btn-sm" style={{ display: "flex", width: "100%", justifyContent: "flex-start", textTransform: "none" }} onClick={() => { p.onPick(b.id); p.onClose(); }}>
          {b.label}
        </button>
      ))}
      <div className="up tert" style={{ fontSize: 10, padding: "4px 6px" }}>Agent outputs <span style={{ textTransform: "none" }}>(sets pipeline position)</span></div>
      {agents.filter(([id, a]) => !p.chosen.has(`agent:${id}`) && match(a.name)).map(([id, a]) => (
        <button key={id} className="btn btn-ghost btn-sm" style={{ display: "flex", width: "100%", justifyContent: "flex-start", textTransform: "none" }} onClick={() => { p.onPick(`agent:${id}`); p.onClose(); }}>
          {a.name} <span className="tert" style={{ marginLeft: 6, fontSize: 10.5 }}>agent:{id}</span>
        </button>
      ))}
      <div className="up tert" style={{ fontSize: 10, padding: "4px 6px" }}>Tools</div>
      {["tool:prior_versions"].filter((t) => !p.chosen.has(t) && match(t)).map((t) => (
        <button key={t} className="btn btn-ghost btn-sm" style={{ display: "flex", width: "100%", justifyContent: "flex-start", textTransform: "none" }} onClick={() => { p.onPick(t); p.onClose(); }}>
          Prior document versions <span className="tert" style={{ marginLeft: 6, fontSize: 10.5 }}>{t}</span>
        </button>
      ))}
      <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center" }}>
        <span className="tert" style={{ fontSize: 11 }}>Custom feed:</span>
        <input
          className="inp"
          placeholder="feed:name"
          style={{ flex: 1, fontSize: 12 }}
          onKeyDown={(e) => {
            const v = (e.target as HTMLInputElement).value.trim();
            if (e.key === "Enter" && v) {
              p.onPick(v.startsWith("feed:") ? v : `feed:${v}`);
              p.onClose();
            }
          }}
        />
      </div>
    </div>
  );
}

function inputLabel(id: string, meta: WorkbenchMeta, bundle: BundleMapV2): string {
  const b = meta.builtinInputs.find((x) => x.id === id);
  if (b) return b.label;
  if (id.startsWith("agent:")) return `↳ ${bundle[id.slice(6)]?.name ?? id.slice(6)}`;
  if (id === "tool:prior_versions") return "Prior document versions";
  return id;
}

export function AgentBuilder(p: AgentBuilderProps): JSX.Element {
  const [def, setDef] = useState<AgentDefV2>(() => JSON.parse(JSON.stringify(p.initial)));
  const [condMenuFor, setCondMenuFor] = useState<number | "new" | null>(null);
  const [inputPickerOpen, setInputPickerOpen] = useState(false);
  const edit = (patch: Partial<AgentDefV2>): void => setDef((d) => ({ ...d, ...patch }));
  const ro = p.readOnly;

  const signalLabel = (id: string): string => p.meta.signals.find((s) => s.id === id)?.label ?? id;
  const summary = conditionSummary(def.cond, signalLabel);
  const unbound = unboundTokens(def, p.meta);
  const profileIssues = Object.entries(def.modelProfiles || {}).flatMap(([model, profile]) => {
    const tokens = unboundTokens({ ...def, prompt: profile.instructions || "" }, p.meta);
    return tokens.length ? [`${model} profile has unbound prompt tokens: ${tokens.map((t) => `{${t}}`).join(", ")}`] : [];
  });
  const destIssues = destinationIssues(def);
  const returnsFields = parseReturnsFields(def.returns);
  const problems: string[] = [
    ...(unbound.length ? [`Unbound prompt tokens: ${unbound.map((t) => `{${t}}`).join(", ")}`] : []),
    ...profileIssues,
    ...destIssues,
  ];
  const canSave = !ro && problems.length === 0 && def.name.trim().length > 0;

  function editActiveProfile(patch: { instructions?: string; tau?: number; maxTokens?: number }): void {
    setDef((current) => {
      const profiles = { ...(current.modelProfiles || {}) };
      const profile = { ...(profiles[current.model] || {}), ...patch };
      if (!profile.instructions?.trim()) delete profile.instructions;
      if (profile.tau === undefined) delete profile.tau;
      if (profile.maxTokens === undefined) delete profile.maxTokens;
      if (Object.keys(profile).length) profiles[current.model] = profile;
      else delete profiles[current.model];
      return { ...current, modelProfiles: Object.keys(profiles).length ? profiles : undefined };
    });
  }

  /* ---- test run ---- */
  const [testCorpusId, setTestCorpusId] = useState<string>(p.corpuses[0]?.id ?? "");
  const testCorpus = p.corpuses.find((c) => c.id === testCorpusId) ?? null;
  const syncedEmails = (testCorpus?.emails ?? []).filter((e) => e.serverId);
  const [testEmailId, setTestEmailId] = useState<number | null>(null);
  const [testing, setTesting] = useState(false);
  const [testProgress, setTestProgress] = useState(0);
  const [testErr, setTestErr] = useState<string | null>(null);
  const [testRes, setTestRes] = useState<ExecResult | null>(null);
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (testEmailId == null && syncedEmails.length) setTestEmailId(syncedEmails[0].id);
  }, [testCorpusId, syncedEmails.length, testEmailId]);
  const myId = p.agentId ?? slugify(def.name);
  const myRecord: ExecCheckRecord | null =
    testRes?.checks.find((c) => c.id === myId) ?? null;

  async function runTest(): Promise<void> {
    const email = syncedEmails.find((e) => e.id === testEmailId);
    if (!email?.serverId) return;
    setTesting(true);
    setTestProgress(0);
    setTestErr(null);
    setTestRes(null);
    try {
      const res = await p.onTestRun(myId, def, email.serverId, () => setTestProgress((n) => n + 1));
      setTestRes(res);
      setStep(0);
    } catch (e) {
      setTestErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  }

  const modeChip = ro ? "Inspecting version — read-only" : p.agentId ? "Editing draft" : "Creating in draft";

  const condRow = (row: AgentCondition, i: number): JSX.Element => (
    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      {i === 0 ? (
        <span className="mono tert" style={{ width: 44, textAlign: "right", fontSize: 12 }}>when</span>
      ) : (
        <button
          className="mono"
          disabled={ro}
          title="Toggle and/or — 'and' binds tighter than 'or'"
          style={{ width: 44, textAlign: "right", fontSize: 12, color: row.j === "or" ? AMBER : "var(--muted)", background: "none" }}
          onClick={() => setDef((d) => ({ ...d, cond: d.cond.map((r, k) => (k === i ? { ...r, j: r.j === "and" ? "or" : "and" } : r)) }))}
        >
          {row.j}
        </button>
      )}
      {row.t === "det" ? (
        <div style={{ position: "relative" }}>
          <button
            className="btn btn-sm"
            disabled={ro}
            style={{ textTransform: "none" }}
            onClick={() => setCondMenuFor(condMenuFor === i ? null : i)}
          >
            {row.neg ? "not " : ""}{signalLabel(row.c)} <ChevronDown size={12} />
          </button>
          {condMenuFor === i && (
            <CondMenu
              meta={p.meta}
              onPick={(id) => setDef((d) => ({ ...d, cond: d.cond.map((r, k) => (k === i ? { ...r, c: id } : r)) }))}
              onClose={() => setCondMenuFor(null)}
            />
          )}
        </div>
      ) : (
        <input
          className="inp"
          disabled={ro}
          value={row.c}
          placeholder="Describe the condition in plain language"
          style={{ flex: 1, borderColor: AMBER, fontSize: 12.5 }}
          onChange={(e) => setDef((d) => ({ ...d, cond: d.cond.map((r, k) => (k === i ? { ...r, c: e.target.value } : r)) }))}
        />
      )}
      {row.t === "sem" && (
        <span className="tchip" style={{ color: AMBER }} title="Judged by the semantic evaluator (Settings)">
          {p.evaluatorModel.split("/").pop()}
        </span>
      )}
      {!ro && (
        <button className="icon-btn" title="Delete condition" onClick={() => setDef((d) => ({ ...d, cond: d.cond.filter((_, k) => k !== i) }))}>
          <X size={13} />
        </button>
      )}
    </div>
  );

  return (
    <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <input
          className="inp"
          disabled={ro}
          value={def.name}
          onChange={(e) => edit({ name: e.target.value })}
          placeholder="Agent name"
          style={{ fontWeight: 600, fontSize: 15, flex: "1 1 220px" }}
        />
        <select className="sel" disabled={ro} value={def.role} onChange={(e) => edit({ role: e.target.value as AgentDefV2["role"] })} title="Cleaner produces the working body; Router files the email; everything else is a plain agent.">
          {(["agent", "cleaner", "router"] as const).map((r) => (
            <option key={r} value={r}>{ROLE_LABEL[r]}</option>
          ))}
        </select>
        <button
          type="button"
          className={"sw" + (def.enabled ? " on" : "")}
          disabled={ro}
          aria-pressed={def.enabled}
          onClick={() => !ro && edit({ enabled: !def.enabled })}
        >
          <span className="tgl" /> Enabled
        </button>
        <span className="chip" style={ro ? { background: "var(--sem-soft)", color: "var(--sem)" } : undefined}>{modeChip}</span>
        <button className="icon-btn" onClick={p.onClose} title="Close">
          <X size={16} />
        </button>
      </div>
      <input className="inp" disabled={ro} value={def.desc} onChange={(e) => edit({ desc: e.target.value })} placeholder="What this agent does, in one sentence" style={{ marginBottom: 16, fontSize: 12.5 }} />

      <div className="scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 4 }}>
        {/* Fires when */}
        <Section title="Fires when" note="deterministic signals are free; semantic rows cost one small-model call per email">
          <div style={{ fontSize: 12.5, marginBottom: 8 }}>
            <span className="tert">Fires when: </span>
            <span style={{ fontWeight: 500 }}>{summary}</span>
          </div>
          {def.cond.map(condRow)}
          {!ro && (
            <div className="addrow" style={{ display: "flex", gap: 8, position: "relative" }}>
              <div style={{ position: "relative" }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setCondMenuFor(condMenuFor === "new" ? null : "new")}>
                  <Plus size={13} /> condition
                </button>
                {condMenuFor === "new" && (
                  <CondMenu
                    meta={p.meta}
                    onPick={(id) => setDef((d) => ({ ...d, cond: [...d.cond, { j: "and", t: "det", c: id }] }))}
                    onClose={() => setCondMenuFor(null)}
                  />
                )}
              </div>
              <button className="btn btn-ghost btn-sm" style={{ color: AMBER }} onClick={() => setDef((d) => ({ ...d, cond: [...d.cond, { j: "and", t: "sem", c: "" }] }))}>
                <Plus size={13} /> semantic condition
              </button>
            </div>
          )}
        </Section>

        {/* Uses */}
        <Section title="Uses" note="agent-output inputs place this agent after its providers — pipeline position derives from these">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", position: "relative" }}>
            {def.inputs.map((inp) => (
              <span key={inp} className={inp.startsWith("agent:") ? "tokpill agent" : inp.startsWith("feed:") || inp.startsWith("tool:") ? "tokpill feed" : "tokpill"} style={{ fontFamily: "var(--sans)" }}>
                {inputLabel(inp, p.meta, p.bundle)}
                {!ro && (
                  <button className="rm" style={{ background: "none", display: "inline-flex", color: "inherit" }} onClick={() => setDef((d) => ({ ...d, inputs: d.inputs.filter((x) => x !== inp) }))}>
                    <X size={11} />
                  </button>
                )}
              </span>
            ))}
            {!ro && (
              <div style={{ position: "relative" }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setInputPickerOpen((v) => !v)}>
                  <Plus size={13} /> input
                </button>
                {inputPickerOpen && (
                  <InputPicker
                    meta={p.meta}
                    bundle={p.bundle}
                    selfId={p.agentId}
                    chosen={new Set(def.inputs)}
                    onPick={(id) => setDef((d) => ({ ...d, inputs: [...d.inputs, id] }))}
                    onClose={() => setInputPickerOpen(false)}
                  />
                )}
              </div>
            )}
          </div>
        </Section>

        {/* Prompt */}
        <Section title="Prompt" note="tokens in {braces} bind to declared inputs; unbound tokens block save">
          {(["SYSTEM", "USER"] as const).map((part) => {
            const m = def.prompt.match(/^SYSTEM:\n([\s\S]*?)\n\nUSER:\n([\s\S]*)$/);
            const [sys, user] = m ? [m[1], m[2]] : ["", def.prompt];
            const val = part === "SYSTEM" ? sys : user;
            return (
              <div key={part} style={{ marginBottom: 8 }}>
                <div className="up tert" style={{ fontSize: 10, marginBottom: 3 }}>{part}</div>
                <textarea
                  className="inp tarea mono"
                  disabled={ro}
                  style={{ minHeight: part === "SYSTEM" ? 130 : 90, fontSize: 12, lineHeight: 1.5 }}
                  value={val}
                  onChange={(e) => {
                    const next = part === "SYSTEM" ? `SYSTEM:\n${e.target.value}\n\nUSER:\n${user}` : `SYSTEM:\n${sys}\n\nUSER:\n${e.target.value}`;
                    edit({ prompt: next });
                  }}
                />
              </div>
            );
          })}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
            <span className="tert" style={{ fontSize: 11 }}>Tokens:</span>
            {promptTokens(def.prompt).map((t) => (
              <span key={t} className={tokClass(p.meta.tokenRequirements[t], unbound.includes(t))} title={unbound.includes(t) ? "No declared input provides this token" : `bound via ${p.meta.tokenRequirements[t] || "agent inputs"}`}>
                {"{"}{t}{"}"}
              </span>
            ))}
            {promptTokens(def.prompt).length === 0 && <span className="tert" style={{ fontSize: 11 }}>(none)</span>}
          </div>
        </Section>

        {/* Returns */}
        <Section title="Returns" note="the example JSON is the contract — outputs failing it twice are parked, visibly">
          <textarea className="inp tarea mono" disabled={ro} style={{ minHeight: 110, fontSize: 12, lineHeight: 1.5 }} value={def.returns} onChange={(e) => edit({ returns: e.target.value })} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", margin: "6px 0" }}>
            <span className="tert" style={{ fontSize: 11 }}>Pipeline reads:</span>
            {returnsFields ? (
              Object.entries(returnsFields).map(([k, kind]) => (
                <span key={k} className="chip mono" style={["conf", "out"].includes(k) ? { color: "var(--fg)" } : undefined}>
                  {k}:{kind}
                </span>
              ))
            ) : (
              <span style={{ color: "var(--red)", fontSize: 11.5 }}>example JSON does not parse</span>
            )}
          </div>
          <div className="tert" style={{ fontSize: 11.5, lineHeight: 1.5, marginBottom: 10 }}>
            <span className="mono">conf</span> is gated by τ ({tauForModel(def).toFixed(2)}): below it the output is parked — visible in the run, applied nowhere. <span className="mono">out</span> is the one-line summary every surface shows.
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginBottom: 10 }}>
            <span style={{ fontWeight: 600 }}>Strict labeled sections</span>
            <input className="inp" disabled={ro} placeholder="section_one, section_two" value={(def.strictSections || []).join(", ")} onChange={(e) => edit({ strictSections: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} style={{ width: 320, fontSize: 12 }} />
          </label>
          <span className="cap" style={{ fontSize: 10.5, marginBottom: 6 }}>Where the output goes</span>
          {/* Downstream — locked on */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12.5 }}>
            <input type="checkbox" checked disabled title="Downstream agents always receive the output — there is no off switch." />
            <span style={{ fontWeight: 600 }}>Downstream agents</span>
            <span className="tert" style={{ fontSize: 11.5 }}>always on — later agents may declare this output as an input</span>
          </div>
          {/* Tray */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12.5, flexWrap: "wrap" }}>
            <input type="checkbox" disabled={ro} checked={!!def.dest.tray} onChange={(e) => edit({ dest: { ...def.dest, tray: e.target.checked ? { kind: "notice", gate: false } : undefined } })} />
            <span style={{ fontWeight: 600 }}>Lawyer Review Tray</span>
            {def.dest.tray && (
              <>
                <select className="sel" disabled={ro} value={def.dest.tray.kind} onChange={(e) => edit({ dest: { ...def.dest, tray: { ...def.dest.tray!, kind: e.target.value as never } } })} style={{ fontSize: 12 }}>
                  <option value="decision">decision (approve / reject)</option>
                  <option value="choice">pick one</option>
                  <option value="input">needs your words</option>
                  <option value="notice">FYI</option>
                </select>
                <select className="sel" disabled={ro} value={def.dest.tray.gate ? "gate" : "open"} onChange={(e) => edit({ dest: { ...def.dest, tray: { ...def.dest.tray!, gate: e.target.value === "gate" } } })} style={{ fontSize: 12 }}>
                  <option value="open">non-blocking</option>
                  <option value="gate">gate — holds one named value</option>
                </select>
                {def.dest.tray.gate && (
                  <input className="inp" disabled={ro} placeholder="held field (blank = escalation's own field)" value={def.dest.tray.holdField ?? ""} onChange={(e) => edit({ dest: { ...def.dest, tray: { ...def.dest.tray!, holdField: e.target.value || undefined } } })} style={{ fontSize: 12, width: 220 }} />
                )}
                <span className="tert" style={{ fontSize: 11, width: "100%" }}>
                  New escalations are matched against pending items (one cheap model call, conservative — duplicates merge with a recurrence note); agents see their pending items and may supersede one when later evidence answers it.
                </span>
              </>
            )}
          </div>
          {/* SA */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12.5 }}>
            <input type="checkbox" disabled={ro} checked={!!def.dest.sa} onChange={(e) => edit({ dest: { ...def.dest, sa: e.target.checked || undefined } })} />
            <span style={{ fontWeight: 600 }}>Senior Associate</span>
            <span className="tert" style={{ fontSize: 11.5 }}>dispatches a work package to the SA service when this agent produces work</span>
          </div>
          {/* Memo */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12.5 }}>
            <input type="checkbox" disabled={ro} checked={!!def.dest.memo} onChange={(e) => edit({ dest: { ...def.dest, memo: e.target.checked || undefined } })} />
            <span style={{ fontWeight: 600 }}>Deal Memo</span>
            <span className="tert" style={{ fontSize: 11.5 }}>folds <span className="mono">facts</span> into the matter memo</span>
          </div>
          {/* Trackers */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12.5, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600 }}>Trackers</span>
            <button className="btn" type="button" disabled={ro} onClick={() => edit({ dest: { ...def.dest, tracker: undefined, trackers: [...(def.dest.trackers || (def.dest.tracker ? [def.dest.tracker] : [])), { key: "custom", resultKey: "entries", matchOn: ["title"] }] } })}>+ Add tracker</button>
            {(def.dest.trackers || (def.dest.tracker ? [def.dest.tracker] : [])).map((tracker, i, all) => (
              <div key={`${i}-${tracker.key}`} style={{ display: "flex", gap: 6, width: "100%", marginLeft: 22 }}>
                <input className="inp" disabled={ro} title="Tracker state key" value={tracker.key} onChange={(e) => edit({ dest: { ...def.dest, tracker: undefined, trackers: all.map((t, at) => at === i ? { ...t, key: e.target.value } : t) } })} style={{ fontSize: 12, width: 110 }} />
                <input className="inp" disabled={ro} title="Dotted path to the array in Returns" value={tracker.resultKey} onChange={(e) => edit({ dest: { ...def.dest, tracker: undefined, trackers: all.map((t, at) => at === i ? { ...t, resultKey: e.target.value } : t) } })} style={{ fontSize: 12, width: 190 }} />
                <input className="inp" disabled={ro} title="Merge-identity fields (comma-separated)" value={tracker.matchOn.join(",")} onChange={(e) => edit({ dest: { ...def.dest, tracker: undefined, trackers: all.map((t, at) => at === i ? { ...t, matchOn: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } : t) } })} style={{ fontSize: 12, width: 150 }} />
                <button className="btn" type="button" disabled={ro} onClick={() => edit({ dest: { ...def.dest, tracker: undefined, trackers: all.filter((_, at) => at !== i) } })}>Remove</button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12.5 }}>
            <input type="checkbox" disabled={ro} checked={!!def.dest.stateOfPlay} onChange={(e) => edit({ dest: { ...def.dest, stateOfPlay: e.target.checked || undefined } })} />
            <span style={{ fontWeight: 600 }}>Deterministic State of Play</span>
            <span className="tert" style={{ fontSize: 11.5 }}>builds the dashboard from accumulated outputs without another model call</span>
          </div>
          {destIssues.length > 0 && (
            <div style={{ color: "var(--red)", fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
              {destIssues.map((i) => (
                <div key={i}>• {i}</div>
              ))}
            </div>
          )}
        </Section>

        {/* Runs on */}
        <Section title="Runs on">
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <select className="sel" disabled={ro} value={def.model} onChange={(e) => edit({ model: e.target.value })}>
              {!p.allowlist.includes(def.model) && <option value={def.model}>{def.model} (not on allowlist)</option>}
              {p.allowlist.map((m) => (
                <option key={m} value={m}>{p.models.find((x) => x.id === m)?.name ?? m}</option>
              ))}
            </select>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
              Shared τ
              <input className="inp numin" type="number" step="0.05" min="0" max="1" disabled={ro} value={def.tau} onChange={(e) => edit({ tau: Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)) })} />
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
              Shared max tokens
              <input className="inp numin" type="number" step="32" min="64" disabled={ro} style={{ width: 90 }} value={def.maxTokens} onChange={(e) => edit({ maxTokens: Math.max(64, parseInt(e.target.value, 10) || 64) })} />
            </label>
          </div>
          <div className="card" style={{ marginTop: 10, padding: 10, background: "var(--bg-subtle, var(--bg))" }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 3 }}>
              Model-specific instructions
            </div>
            <div className="tert" style={{ fontSize: 11.5, lineHeight: 1.45, marginBottom: 7 }}>
              The shared prompt above always applies. These instructions are added automatically whenever this agent runs on {p.models.find((x) => x.id === def.model)?.name ?? def.model}.
            </div>
            <textarea
              className="inp tarea"
              disabled={ro}
              placeholder="Optional calibration for this model, for example stricter conflict detection or formatting guidance."
              value={def.modelProfiles?.[def.model]?.instructions ?? ""}
              onChange={(e) => editActiveProfile({ instructions: e.target.value || undefined })}
              style={{ minHeight: 80, fontSize: 12, lineHeight: 1.5 }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, marginTop: 8 }}>
              <input
                type="checkbox"
                disabled={ro}
                checked={def.modelProfiles?.[def.model]?.tau !== undefined || def.modelProfiles?.[def.model]?.maxTokens !== undefined}
                onChange={(e) => editActiveProfile(e.target.checked ? { tau: def.tau, maxTokens: def.maxTokens } : { tau: undefined, maxTokens: undefined })}
              />
              Override τ and max tokens for this model
            </label>
            {(def.modelProfiles?.[def.model]?.tau !== undefined || def.modelProfiles?.[def.model]?.maxTokens !== undefined) && (
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
                  Profile τ
                  <input className="inp numin" type="number" step="0.05" min="0" max="1" disabled={ro} value={tauForModel(def)} onChange={(e) => editActiveProfile({ tau: Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)) })} />
                </label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
                  Profile max tokens
                  <input className="inp numin" type="number" step="32" min="64" disabled={ro} style={{ width: 90 }} value={maxTokensForModel(def)} onChange={(e) => editActiveProfile({ maxTokens: Math.max(64, parseInt(e.target.value, 10) || 64) })} />
                </label>
              </div>
            )}
          </div>
        </Section>

        {/* Test */}
        {!ro && (
          <Section title="Test" note="saves the draft, runs the whole pipeline on one email, and steps through this agent">
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              <select className="sel" value={testCorpusId} onChange={(e) => { setTestCorpusId(e.target.value); setTestEmailId(null); }}>
                {p.corpuses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <select className="sel" value={testEmailId ?? ""} onChange={(e) => setTestEmailId(Number(e.target.value))}>
                {syncedEmails.map((e) => (
                  <option key={e.id} value={e.id}>#{e.id} — {e.subject.slice(0, 48)}</option>
                ))}
              </select>
              <button className="btn btn-primary btn-sm" disabled={testing || !syncedEmails.length} onClick={() => void runTest()}>
                {testing ? <Loader2 size={13} className="spinrot" /> : <Play size={13} />}
                {testing ? `Running… ${testProgress} agent${testProgress === 1 ? "" : "s"}` : "Run test"}
              </button>
              {!syncedEmails.length && <span className="tert" style={{ fontSize: 11.5 }}>corpus not synced to the backend yet</span>}
            </div>
            {testErr && <div style={{ color: "var(--red)", fontSize: 12.5 }}>{testErr}</div>}
            {testRes && myRecord && (
              <div className="card" style={{ padding: 10 }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  {["Trigger", "Prompt", "Response", "Effects"].map((s, i) => (
                    <button key={s} className={"btn btn-sm " + (step === i ? "btn-primary" : "btn-ghost")} onClick={() => setStep(i)}>
                      {i + 1}. {s}
                    </button>
                  ))}
                </div>
                {step === 0 && (
                  <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                    {myRecord.skipped && <div style={{ color: AMBER, marginBottom: 6 }}>Did not fire — {myRecord.reason}</div>}
                    {(myRecord.trace?.gate ?? []).map((g, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                        <span className="mono" style={{ color: g.pass ? "var(--green)" : "var(--red)" }}>{g.pass ? "✓" : "✗"}</span>
                        <span style={{ color: g.t === "sem" ? AMBER : undefined }}>{g.row}</span>
                        <span className="tert" style={{ fontSize: 11.5 }}>{g.why}</span>
                      </div>
                    ))}
                    {(myRecord.trace?.gate ?? []).length === 0 && !myRecord.skipped && <div className="tert">Every email — no conditions.</div>}
                  </div>
                )}
                {step === 1 && (
                  <div>
                    {myRecord.trace?.systemPrompt != null ? (
                      <>
                        <div className="up tert" style={{ fontSize: 10 }}>SYSTEM (exact input)</div>
                        <pre className="mono" style={{ fontSize: 11, whiteSpace: "pre-wrap", maxHeight: 180, overflowY: "auto", background: "var(--subtle)", padding: 8, borderRadius: 6 }}>{myRecord.trace.systemPrompt}</pre>
                        <div className="up tert" style={{ fontSize: 10, marginTop: 6 }}>USER (exact input)</div>
                        <pre className="mono" style={{ fontSize: 11, whiteSpace: "pre-wrap", maxHeight: 220, overflowY: "auto", background: "var(--subtle)", padding: 8, borderRadius: 6 }}>{myRecord.trace.userPrompt}</pre>
                      </>
                    ) : (
                      <div className="tert" style={{ fontSize: 12.5 }}>No prompt — the agent did not fire.</div>
                    )}
                  </div>
                )}
                {step === 2 && (
                  <div style={{ fontSize: 12.5 }}>
                    {myRecord.raw != null && (
                      <>
                        <div className="up tert" style={{ fontSize: 10 }}>Raw response</div>
                        <pre className="mono" style={{ fontSize: 11, whiteSpace: "pre-wrap", maxHeight: 160, overflowY: "auto", background: "var(--subtle)", padding: 8, borderRadius: 6 }}>{myRecord.raw}</pre>
                      </>
                    )}
                    <div style={{ margin: "6px 0" }}>
                      Contract check:{" "}
                      {myRecord.parked ? (
                        <span style={{ color: AMBER }}>parked — {(myRecord.trace?.problems ?? []).join("; ") || myRecord.out}</span>
                      ) : myRecord.ok ? (
                        <span style={{ color: "var(--green)" }}>valid · conf {myRecord.conf.toFixed(2)} vs τ {def.tau.toFixed(2)}</span>
                      ) : (
                        <span style={{ color: "var(--red)" }}>{myRecord.out}</span>
                      )}
                    </div>
                    <span className="mono tert" style={{ fontSize: 11 }}>
                      {myRecord.model} · {myRecord.usage.input_tokens}→{myRecord.usage.output_tokens} tok · ${myRecord.usage.cost.toFixed(4)}
                    </span>
                  </div>
                )}
                {step === 3 && (
                  <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>
                    {(myRecord.trace?.effects ?? []).length ? (
                      (myRecord.trace?.effects ?? []).map((e) => <div key={e} className="mono">{e}</div>)
                    ) : (
                      <div className="tert">No destination effects on this email (output flows downstream only).</div>
                    )}
                    {testRes.tray.filter((t) => t.agentId === myId).map((t) => (
                      <div key={t.id}>tray → {t.kind}: {t.title}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Section>
        )}
      </div>

      {/* Footer */}
      <div className="dfoot">
        {!ro && (
          <button className="btn btn-primary btn-sm" disabled={!canSave} onClick={() => p.onSave(myId, def)}>
            Save to draft
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={p.onClose}>Cancel</button>
        {!ro && p.agentId && (
          <button className="btn btn-ghost btn-sm" style={{ color: "var(--bad)", borderColor: "var(--bad-soft)" }} onClick={() => p.onRemove(p.agentId!)}>
            <Trash2 size={13} /> Remove from bundle
          </button>
        )}
        <span className="hint">
          {problems.length > 0 && !ro ? (
            <span style={{ color: "var(--bad)" }} title={problems.join("\n")}>
              {problems.length} problem{problems.length === 1 ? "" : "s"} block saving
            </span>
          ) : (
            "Changes stage in the draft — Save version to freeze"
          )}
        </span>
      </div>
    </div>
  );
}
